/**
 * `useAppointments` / `useAppointment` — live reads of the CRM
 * `appointments` collection.
 *
 * Mirrors the mobile `src/features/crm/useAppointments.ts` hook.
 * Firestore rule (`firestore.rules → match /appointments/{id}`) gates
 * reads to org members.
 *
 * Sort: `scheduledAt` asc (soonest first). Appointments without a
 * `scheduledAt` (legacy) sink to the end.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import { useFullOrganization } from '@/lib/useFullOrganization';

/** Legacy "type" field — kept in the union for backward-compat reads
 *  on existing docs. New writes use `meetingPurpose` + `locationMode`
 *  instead, per the redesigned create form. */
// AppointmentType + AppointmentStatus moved to
// `@shared/perms/appointment` so mobile and web share one copy.
// Re-exported here so existing imports keep working.
// AppointmentType / AppointmentStatus / MeetingPurpose /
// LocationMode all moved to `@shared/perms/appointment` so mobile
// and web share one definition.
import type {
  AppointmentType,
  AppointmentStatus,
  MeetingPurpose,
  LocationMode,
} from '@shared/perms';
export type {
  AppointmentType,
  AppointmentStatus,
  MeetingPurpose,
  LocationMode,
};

export type AppointmentItem = {
  id: string;
  orgId: string;
  leadId?: string;
  clientName?: string;
  clientPhone?: string;
  clientAddress?: string;
  /** Legacy. Prefer `meetingPurpose` for new writes. */
  type: AppointmentType;
  /** NEW — what the meeting is about. */
  meetingPurpose?: MeetingPurpose;
  /** NEW — where the meeting happens. */
  locationMode?: LocationMode;
  title: string;
  scheduledAt?: { toMillis(): number } | null;
  durationMins?: number;
  location?: string;
  attendees?: string[];
  /** NEW per redesigned form — staff member responsible. Denormalised
   *  to a display string so the list + preview render without a
   *  members lookup. */
  assignedToName?: string;
  status: AppointmentStatus;
  notes?: string;
  outcome?: string;
  createdAt?: { toMillis(): number } | null;
  updatedAt?: { toMillis(): number } | null;
  createdBy?: string;
};

export type UseAppointmentsResult = {
  appointments: AppointmentItem[];
  loading: boolean;
  error: string | null;
  orgId: string | null;
};

export function useAppointments(): UseAppointmentsResult {
  const { org, loading: orgLoading } = useFullOrganization();
  const [items, setItems] = useState<AppointmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = org?.id ?? null;

  useEffect(() => {
    if (orgLoading) return;
    if (!orgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(collection(db, 'appointments'), where('orgId', '==', orgId)),
      (snap) => {
        setItems(
          snap.docs.map((d) => ({
            ...(d.data() as AppointmentItem),
            id: d.id,
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useAppointments] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, orgLoading]);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const am = a.scheduledAt?.toMillis?.() ?? Number.POSITIVE_INFINITY;
        const bm = b.scheduledAt?.toMillis?.() ?? Number.POSITIVE_INFINITY;
        return am - bm;
      }),
    [items],
  );

  return { appointments: sorted, loading, error, orgId };
}

// ───── Single-doc hook ─────

export type UseAppointmentResult = {
  appointment: AppointmentItem | null;
  loading: boolean;
  error: string | null;
};

export function useAppointment(apptId: string | undefined): UseAppointmentResult {
  const [appointment, setAppointment] = useState<AppointmentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apptId) {
      setAppointment(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, 'appointments', apptId),
      (snap) => {
        if (!snap.exists()) setAppointment(null);
        else setAppointment({ ...(snap.data() as AppointmentItem), id: snap.id });
        setLoading(false);
      },
      (err) => {
        console.warn('[useAppointment] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [apptId]);

  return { appointment, loading, error };
}

// ───── Display helpers ─────

export function appointmentTypeLabel(t: AppointmentType): string {
  switch (t) {
    case 'site_visit':      return 'Site visit';
    case 'office_meeting':  return 'Office meeting';
    case 'virtual_call':    return 'Virtual call';
    case 'other':           return 'Other';
  }
}

export function appointmentStatusLabel(s: AppointmentStatus): string {
  switch (s) {
    case 'scheduled':  return 'Scheduled';
    case 'completed':  return 'Completed';
    case 'cancelled':  return 'Cancelled';
    case 'no_show':    return 'No-show';
  }
}

export function meetingPurposeLabel(p: MeetingPurpose): string {
  switch (p) {
    case 'initial_consultation': return 'Initial Consultation';
    case 'site_measurement':     return 'Site Measurement';
    case 'design_presentation':  return 'Design Presentation';
    case 'site_visit':           return 'Site Visit';
    case 'review':               return 'Progress Review';
    case 'handover':             return 'Handover';
    case 'other':                return 'Other';
  }
}

export function locationModeLabel(m: LocationMode): string {
  switch (m) {
    case 'site':   return 'At Client Site / Property';
    case 'office': return 'At Our Office';
    case 'online': return 'Online / Video Call';
    case 'other':  return 'Other';
  }
}

// Form option arrays computed from the canonical shared enums via
// `toFormOptions`. Labels match shared exactly today — adapters
// stay in place so adding overrides later is one keystroke.
import { MEETING_PURPOSES, LOCATION_MODES } from '@shared/perms';
import { toFormOptions } from '@/lib/formOptions';

export const MEETING_PURPOSE_OPTIONS = toFormOptions(MEETING_PURPOSES);
export const LOCATION_MODE_OPTIONS = toFormOptions(LOCATION_MODES);

/** Buckets for the filter chip row + right-rail cards. */
export function appointmentBucket(a: AppointmentItem): 'upcoming' | 'past' | 'today' | 'cancelled' {
  if (a.status === 'cancelled' || a.status === 'no_show') return 'cancelled';
  const ms = a.scheduledAt?.toMillis?.();
  if (!ms) return 'upcoming';
  const now = Date.now();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = today.getTime() + 86400000;
  if (ms >= today.getTime() && ms < tomorrow) return 'today';
  if (ms < now) return 'past';
  return 'upcoming';
}
